import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, CreditCard, Calendar, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

import { API_URL } from "@/utils/api";
const CALENDAR_LINK = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ07qBLS9ridhleyYbHgoh8tK2GytA06zF8p0i5zF3IfiDRKXQGW17EZmfPsPcjIyZXiw-BjNNVw";

// Core plans
const CORE_PLANS = [
  { id: "starter", name: "Starter", monthly: 0, annual: 0, description: "For creators just getting started", features: ["Up to $25K annual transactions", "Basic bookkeeping", "Invoice generator", "Expense tracking"], highlight: null, cta: "Start Free" },
  { id: "growth", name: "Growth", monthly: 149, annual: 1490, description: "For growing businesses", features: ["Up to $100K annual transactions", "Full bookkeeping + reconciliation", "KPI dashboards", "Dedicated bookkeeper"], highlight: "Most Popular", cta: "Get Started" },
  { id: "scale", name: "Scale", monthly: 349, annual: 3490, description: "For established businesses", features: ["Unlimited transactions", "Advanced reporting", "Tax strategy planning", "Priority support"], highlight: null, cta: "Get Started" },
  { id: "enterprise", name: "Enterprise", monthly: null, annual: null, description: "For complex operations", features: ["Everything in Scale", "Dedicated account manager", "Custom integrations", "CFO advisory services"], highlight: "Custom", cta: "Contact Sales" }
];

// Advanced plans
const ADVANCED_PLANS = [
  { id: "creator", name: "Creator Plan", monthly: 79, annual: 790, description: "Professional bookkeeping for creators", features: ["Up to 100 transactions/month", "Proper categorization", "Tax-ready records", "Multi-platform payout support"], highlight: "Popular with creators", cta: "Get Started" },
  { id: "solopreneur", name: "Solopreneur", monthly: 99, annual: 990, description: "Essentials for low volume", features: ["Up to 50 transactions/month", "1 bank account", "Monthly reconciliation", "Basic reports"], highlight: null, cta: "Get Started" },
  { id: "dedicated", name: "Dedicated Bookkeeping", monthly: 299, annual: 2990, description: "Full financials + KPI dashboard", features: ["Up to 150 transactions/month", "3 bank accounts", "KPI dashboard", "Dedicated bookkeeper"], highlight: null, cta: "Get Started" },
  { id: "strategic", name: "Strategic Accounting", monthly: 499, annual: 4990, description: "Budgeting & deeper insights", features: ["Up to 300 transactions/month", "5 bank accounts", "Budgeting & forecasting", "Variance analysis"], highlight: null, cta: "Get Started" },
  { id: "ultimate", name: "Ultimate Bundle", monthly: 799, annual: 7990, description: "Advanced reporting, dedicated AM", features: ["Unlimited transactions", "7 bank accounts", "Dedicated account manager", "Priority support"], highlight: null, cta: "Get Started" },
  { id: "vcfo", name: "vCFO", monthly: 1499, annual: 14990, description: "Executive financial leadership", features: ["Monthly CFO strategy calls", "3-year financial models", "Board presentation support", "Everything in Ultimate"], highlight: "Enterprise", cta: "Contact Sales" },
  { id: "allinone", name: "All-in-One", monthly: 1999, annual: 19990, description: "Full-stack finance", features: ["Everything in vCFO", "Custom data integration", "Dedicated finance team", "24/7 priority support"], highlight: "Complete Solution", cta: "Contact Sales" }
];

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get("plan") || "growth");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setIsAuthenticated(response.ok);
    } catch { setIsAuthenticated(false); }
  };

  const handleCheckout = async (planId) => {
    if (planId === "enterprise" || planId === "vcfo" || planId === "allinone") {
      window.open(CALENDAR_LINK, "_blank");
      return;
    }
    if (planId === "starter") {
      navigate("/register");
      return;
    }
    if (!isAuthenticated) { toast.info("Please sign in to continue"); navigate("/login"); return; }
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), "Origin": window.location.origin },
        credentials: "include",
        body: JSON.stringify({ plan_id: planId, billing_cycle: billingCycle })
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || "Failed to create checkout"); }
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const getPrice = (plan) => {
    if (plan.monthly === null) return "Custom";
    if (plan.monthly === 0) return "Free";
    return billingCycle === "annual" ? `$${plan.annual.toLocaleString()}` : `$${plan.monthly}`;
  };

  const getPeriod = (plan) => {
    if (plan.monthly === null || plan.monthly === 0) return "";
    return billingCycle === "annual" ? "/year" : "/month";
  };

  const getSavings = (plan) => {
    if (!plan.monthly || plan.monthly === 0) return 0;
    return Math.round(((plan.monthly * 12 - plan.annual) / (plan.monthly * 12)) * 100);
  };

  const PlanCard = ({ plan, isSelected }) => (
    <div 
      className={`relative bg-white border-2 border-dashed rounded-2xl p-6 transition-all duration-300 cursor-pointer ${
        isSelected ? "border-[#BFFF00] shadow-xl shadow-[#BFFF00]/20 scale-[1.02]" : "border-gray-200 hover:border-[#BFFF00]/50 hover:shadow-lg"
      }`} 
      onClick={() => setSelectedPlan(plan.id)}
    >
      {plan.highlight && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
          plan.highlight === "Most Popular" ? "bg-[#BFFF00] text-[#121212]" : "bg-[#121212] text-white"
        }`}>
          {plan.highlight}
        </div>
      )}
      <h3 className="text-lg font-bold text-[#121212] mb-1 font-['Manrope']">{plan.name}</h3>
      <p className="text-[#121212]/60 text-sm mb-4">{plan.description}</p>
      <div className="mb-4">
        <span className="text-3xl font-bold text-[#121212] font-['Manrope']">{getPrice(plan)}</span>
        <span className="text-[#121212]/60">{getPeriod(plan)}</span>
        {billingCycle === "annual" && plan.monthly > 0 && (
          <p className="text-xs text-[#121212] mt-1 bg-[#BFFF00]/30 inline-block px-2 py-0.5 rounded-full">Save {getSavings(plan)}%</p>
        )}
      </div>
      <ul className="space-y-2 mb-6">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-[#121212]/70">
            <CheckCircle2 className="w-4 h-4 text-[#BFFF00] flex-shrink-0 mt-0.5" />{feature}
          </li>
        ))}
      </ul>
      <Button 
        onClick={(e) => { e.stopPropagation(); handleCheckout(plan.id); }} 
        disabled={loading && selectedPlan === plan.id}
        className={`w-full h-10 rounded-full font-medium transition-all ${
          isSelected 
            ? "bg-[#121212] hover:bg-[#2a2a2a] text-white" 
            : "bg-[#F7FFE5] hover:bg-[#BFFF00]/30 text-[#121212] border border-[#BFFF00]/50"
        }`} 
        data-testid={`checkout-${plan.id}-btn`}
      >
        {loading && selectedPlan === plan.id ? "Processing..." : (
          <>
            {plan.cta === "Get Started" && <CreditCard className="w-4 h-4 mr-2" />}
            {plan.cta === "Contact Sales" && <Calendar className="w-4 h-4 mr-2" />}
            {plan.cta}
          </>
        )}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-[#BFFF00]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#BFFF00] rounded-xl flex items-center justify-center shadow-lg shadow-[#BFFF00]/30">
                <span className="text-[#121212] font-extrabold text-lg font-['Manrope']">F</span>
              </div>
              <span className="font-extrabold text-[#121212] text-xl font-['Manrope']">FinAI</span>
            </Link>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-[#121212]/70 hover:text-[#121212] hover:bg-[#BFFF00]/20 rounded-full">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-[#121212]/70 hover:text-[#121212] hover:bg-[#BFFF00]/20 rounded-full">Login</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="h-10 px-6 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-semibold">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center text-[#121212]/50 hover:text-[#121212] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-[#121212] mb-4 font-['Manrope']">Choose Your Plan</h1>
          <p className="text-lg text-[#121212]/60 max-w-2xl mx-auto mb-8">
            Transparent pricing for every stage of your business. All plans include tax-ready books.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-[#F7FFE5] rounded-full p-1 border border-[#BFFF00]/30">
            <button 
              onClick={() => setBillingCycle("monthly")} 
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billingCycle === "monthly" ? "bg-[#BFFF00] text-[#121212] shadow-sm" : "text-[#121212]/60 hover:text-[#121212]"
              }`} 
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button 
              onClick={() => setBillingCycle("annual")} 
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                billingCycle === "annual" ? "bg-[#BFFF00] text-[#121212] shadow-sm" : "text-[#121212]/60 hover:text-[#121212]"
              }`} 
              data-testid="billing-annual-btn"
            >
              Annual
              <span className="bg-[#121212] text-[#BFFF00] text-xs px-2 py-0.5 rounded-full">Save 17%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Core Plans */}
      <section className="pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CORE_PLANS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} isSelected={selectedPlan === plan.id} />
            ))}
          </div>
        </div>
      </section>

      {/* Show More Plans Toggle */}
      <section className="pb-8 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <Button
            variant="outline"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="rounded-full border-2 border-dashed border-[#BFFF00] text-[#121212] hover:bg-[#BFFF00]/20 gap-2 font-semibold"
            data-testid="show-more-plans-btn"
          >
            <Sparkles className="w-4 h-4" />
            {showAdvanced ? "Show Less Plans" : "Show More Specialized Plans"}
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </section>

      {/* Advanced Plans */}
      {showAdvanced && (
        <section className="pb-16 px-6 lg:px-12 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#121212] mb-2 font-['Manrope']">Specialized Plans</h2>
              <p className="text-[#121212]/60">More options tailored to specific business needs</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {ADVANCED_PLANS.map((plan) => (
                <PlanCard key={plan.id} plan={plan} isSelected={selectedPlan === plan.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Add-ons Section */}
      <section className="pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#F7FFE5] border-2 border-dashed border-[#BFFF00]/50 rounded-2xl p-8 text-center">
            <h3 className="text-lg font-bold text-[#121212] mb-2 font-['Manrope']">Available Add-ons</h3>
            <p className="text-[#121212]/60 mb-4">Payroll • AP/AR Management • Advanced Inventory (COGS, FIFO/LIFO/WA)</p>
            <a href={CALENDAR_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-semibold" data-testid="addons-schedule-btn">
                <Calendar className="w-4 h-4 mr-2" />Schedule a Call to Discuss
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#BFFF00]/20 bg-[#F7FFE5]/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm text-[#121212]/50">
            © 2026 FinAI by Revurge. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
